import { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import { useLiveData, useService } from '@toeverything/infra';
import type { RefObject } from 'react';
import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * What the sidebar lists below its search: the workspace's areas, the user's favorites, or every
 * document with the one changed last on top.
 */
export type DocumentsSidebarView = 'workspace' | 'favorites' | 'recent';

// Stored like the sidebar's other disclosure state: both lists are put away by default.
export const FAVORITES_VIEW_PATH = ['workspaceDocuments', 'favorites'];
export const RECENT_VIEW_PATH = ['workspaceDocuments', 'recent'];

/** The view the user chose last, per workspace. A reveal leads into the workspace's tree. */
export function useDocumentsSidebarView(revealRequestId: number | undefined) {
  const navigation = useService(NavigationPanelService);
  const favoritesAway = useLiveData(navigation.collapsed$(FAVORITES_VIEW_PATH));
  const recentAway = useLiveData(navigation.collapsed$(RECENT_VIEW_PATH));
  const view: DocumentsSidebarView = !favoritesAway
    ? 'favorites'
    : !recentAway
      ? 'recent'
      : 'workspace';
  const show = useCallback(
    (next: DocumentsSidebarView) => {
      navigation.setCollapsed(FAVORITES_VIEW_PATH, next !== 'favorites');
      navigation.setCollapsed(RECENT_VIEW_PATH, next !== 'recent');
    },
    [navigation]
  );
  // A request that is pending when the sidebar arrives counts as new.
  const revealed = useRef<number | undefined>(undefined);
  useLayoutEffect(() => {
    if (revealRequestId !== undefined && revealRequestId !== revealed.current)
      show('workspace');
    revealed.current = revealRequestId;
  }, [revealRequestId, show]);
  return [view, show] as const;
}

/** The views share one scroll viewport; each comes back where the user left it. */
export function useDocumentsSidebarViewScroll(
  view: DocumentsSidebarView,
  anchor: RefObject<HTMLElement | null>
) {
  const offsets = useRef({ workspace: 0, favorites: 0, recent: 0 });
  const shown = useRef(view);
  useLayoutEffect(() => {
    const viewport = anchor.current?.parentElement;
    if (!viewport) return;
    if (shown.current !== view) viewport.scrollTop = offsets.current[view];
    shown.current = view;
    const remember = () => {
      offsets.current[view] = viewport.scrollTop;
    };
    viewport.addEventListener('scroll', remember, { passive: true });
    return () => viewport.removeEventListener('scroll', remember);
  }, [anchor, view]);
}
